import React from 'react';
import { 
    Card, Row, Col, ProgressBar, Badge, 
    ListGroup, Alert 
} from 'react-bootstrap';
import { 
    FaFolder, FaFileAlt, FaBuilding, FaChartBar,
    FaArchive, FaLayerGroup, FaChartLine
} from 'react-icons/fa';

const FolderStatistics = ({ 
    statistics, 
    departments, 
    folders, 
    forms 
}) => {
    const renderDepartmentBreakdown = () => {
        if (!statistics.departmentBreakdown || Object.keys(statistics.departmentBreakdown).length === 0) {
            return (
                <Alert variant="info" className="small mb-0">
                    No department data available
                </Alert>
            );
        }

        return (
            <ListGroup variant="flush">
                {Object.entries(statistics.departmentBreakdown).map(([deptName, stats]) => (
                    <ListGroup.Item key={deptName} className="px-0 py-2">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="fw-bold">{deptName}</small>
                            </div>
                            <div className="d-flex gap-2">
                                <Badge bg="primary" size="sm">
                                    <FaFolder className="me-1" />
                                    {stats.folders || 0}
                                </Badge>
                                <Badge bg="success" size="sm">
                                    <FaFileAlt className="me-1" />
                                    {stats.forms || 0}
                                </Badge>
                            </div>
                        </div>
                        <div className="mt-1">
                            <ProgressBar 
                                size="sm" 
                                now={((stats.forms || 0) / Math.max(statistics.totalForms, 1)) * 100}
                                variant="success"
                                style={{ height: '4px' }}
                            />
                        </div>
                    </ListGroup.Item>
                ))}
            </ListGroup>
        );
    };

    const renderFolderDistribution = () => {
        const rootFolders = folders.filter(f => !f.parent_folder_id);
        const nestedFolders = folders.filter(f => f.parent_folder_id);
        
        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Root Folders</small>
                    <Badge bg="primary">{rootFolders.length}</Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Nested Folders</small>
                    <Badge bg="info">{nestedFolders.length}</Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">Average Depth</small>
                    <Badge bg="secondary">
                        {folders.length > 0 ? (nestedFolders.length / folders.length * 2).toFixed(1) : '0'}
                    </Badge>
                </div>
            </div>
        );
    };

    const renderFormDistribution = () => {
        const assignedForms = forms.filter(f => f.folder_id);
        const unassignedForms = forms.filter(f => !f.folder_id);
        const assignmentRate = forms.length > 0 ? (assignedForms.length / forms.length) * 100 : 0;
        
        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Assigned Forms</small>
                    <Badge bg="success">{assignedForms.length}</Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <small className="text-muted">Unassigned Forms</small>
                    <Badge bg="warning">{unassignedForms.length}</Badge>
                </div>
                <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-muted">Assignment Rate</small>
                        <small className="fw-bold">{assignmentRate.toFixed(1)}%</small>
                    </div>
                    <ProgressBar 
                        now={assignmentRate} 
                        variant={assignmentRate > 80 ? 'success' : assignmentRate > 50 ? 'warning' : 'danger'}
                        size="sm"
                    />
                </div>
            </div>
        );
    };

    const renderQuickInsights = () => {
        const insights = [];
        
        if (statistics.formsWithoutFolder > 0) {
            insights.push({
                type: 'warning',
                icon: FaArchive,
                text: `${statistics.formsWithoutFolder} forms need folder assignment`
            });
        }
        
        if (statistics.totalFolders === 0) {
            insights.push({
                type: 'info',
                icon: FaFolder,
                text: 'Create your first folder to organize forms'
            });
        }
        
        if (Object.keys(statistics.departmentBreakdown).length > 1) {
            insights.push({
                type: 'success',
                icon: FaBuilding,
                text: `Forms organized across ${Object.keys(statistics.departmentBreakdown).length} departments`
            });
        }
        
        if (insights.length === 0) {
            insights.push({
                type: 'success',
                icon: FaChartBar,
                text: 'All forms are properly organized'
            });
        }
        
        return insights.map((insight, index) => (
            <Alert key={index} variant={insight.type} className="py-2 px-3 mb-2 small">
                <insight.icon className="me-2" />
                {insight.text}
            </Alert>
        ));
    };

    return (
        <div className="folder-statistics">
            <Row>
                <Col xs={12}>
                    <Card className="mb-3">
                        <Card.Header className="py-2">
                            <h6 className="mb-0">
                                <FaChartBar className="me-2 text-primary" />
                                Quick Insights
                            </h6>
                        </Card.Header>
                        <Card.Body className="py-2">
                            {renderQuickInsights()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Card className="mb-3">
                        <Card.Header className="py-2">
                            <h6 className="mb-0">
                                <FaBuilding className="me-2 text-primary" />
                                Department Breakdown
                            </h6>
                        </Card.Header>
                        <Card.Body className="py-2">
                            {renderDepartmentBreakdown()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Card className="mb-3">
                        <Card.Header className="py-2">
                            <h6 className="mb-0">
                                <FaLayerGroup className="me-2 text-primary" />
                                Folder Structure
                            </h6>
                        </Card.Header>
                        <Card.Body className="py-2">
                            {renderFolderDistribution()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Card className="mb-3">
                        <Card.Header className="py-2">
                            <h6 className="mb-0">
                                <FaFileAlt className="me-2 text-primary" />
                                Form Organization
                            </h6>
                        </Card.Header>
                        <Card.Body className="py-2">
                            {renderFormDistribution()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col xs={12}>
                    <Card>
                        <Card.Header className="py-2">
                            <h6 className="mb-0">
                                <FaChartLine className="me-2 text-primary" />
                                Organization Health
                            </h6>
                        </Card.Header>
                        <Card.Body className="py-2">
                            <div className="text-center">
                                {statistics.totalFolders > 0 && statistics.formsWithoutFolder === 0 ? (
                                    <div>
                                        <div className="display-6 text-success mb-2">💚</div>
                                        <small className="text-success fw-bold">Excellent Organization</small>
                                        <div className="small text-muted mt-1">
                                            All forms are properly categorized
                                        </div>
                                    </div>
                                ) : statistics.formsWithoutFolder < statistics.totalForms * 0.2 ? (
                                    <div>
                                        <div className="display-6 text-warning mb-2">💛</div>
                                        <small className="text-warning fw-bold">Good Organization</small>
                                        <div className="small text-muted mt-1">
                                            Most forms are categorized
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="display-6 text-danger mb-2">💔</div>
                                        <small className="text-danger fw-bold">Needs Improvement</small>
                                        <div className="small text-muted mt-1">
                                            Many forms need organization
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default FolderStatistics;